/**
 * NPEFixationListPage — danh sách NPE Fixations. Đồng bộ ContractListView.
 * Edit/Delete disable khi (allocations_count > 0).
 */
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Snackbar, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintIcon from '@mui/icons-material/Print'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import RoleGuard from '@/components/RoleGuard'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT, TEXT_TRUNCATE } from '@/theme/responsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useAppSelector } from '../../store'
import { API_BASE_URL } from '../../config/api'
import {
  useDeleteNPEFixationMutation, useGetNPEFixationQuery, useLazyPrintNPEFixationQuery, useListNPEFixationsQuery,
} from '@/store/api/npeFixationsApi'
import type { NPEFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { titleCaseName } from '@/utils/text'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import NPEAllocationAddDialog from './NPEAllocationAddDialog'
import NPEFixationFormDialog from './NPEFixationFormDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

// Inject `@page { size: A4; margin: 0 !important }` để suppress browser-injected
// page header (URL/date) + footer (page number). `!important` override base
// template `_print_base.html` (margin 9mm 11mm). Mobile screen: `.report-page`
// min-height auto để bỏ vùng trống cuối iframe preview (BE reserves 276mm cho A4).
const PURCHASE_PRINT_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 10mm !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
.print-action { display: none !important; }
`.trim()

function buildNPEPrintPreviewHtml(rawHtml: string): string {
  if (!rawHtml) return ''
  const withBase = rawHtml.includes('<base ')
    ? rawHtml
    : rawHtml.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="purchase-print-preview-overrides">${PURCHASE_PRINT_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

type NPEPrintTarget = { fixationId: number; fixationNo: string }

interface NPEFixationPrintDialogProps {
  open: boolean
  printTarget: NPEPrintTarget | null
  canEdit?: boolean
  onClose: () => void
}

/**
 * Print Preview - NPE Fixation (Phụ lục hợp đồng).
 * Access parity: `btnPrint_Click` → `PrintAndUploadReport "rpPurchaseQryNPEFixation"`.
 * Auto-fetches HTML khi mở, renders trong iframe srcDoc; nút Print upload + print
 * qua `printAndUploadReport`.
 */
function NPEFixationPrintDialog({ open, printTarget, canEdit = true, onClose }: NPEFixationPrintDialogProps) {
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewKey, setPreviewKey] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [triggerPrint, { isFetching }] = useLazyPrintNPEFixationQuery()
  const autoPreviewKeyRef = useRef<number | null>(null)

  const fixationId = printTarget?.fixationId ?? 0
  const fixationNo = printTarget?.fixationNo ?? ''

  const handleClosePreview = useCallback(() => {
    setPreviewHtml('')
    setPreviewKey('')
  }, [])

  useEffect(() => {
    if (!open) {
      autoPreviewKeyRef.current = null
      return
    }
    if (!fixationId || autoPreviewKeyRef.current === fixationId) return
    autoPreviewKeyRef.current = fixationId
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await triggerPrint(fixationId).unwrap()
          if (!result.data.html) {
            setErrorMessage('Print preview could not open because the server did not return a document.')
            onClose()
            return
          }
          setPreviewHtml(buildNPEPrintPreviewHtml(result.data.html))
          setPreviewKey(fixationNo || String(fixationId))
          setToastMessage(result.message ?? 'Print preview generated.')
          onClose()
        } catch (err) {
          setErrorMessage(getErrorMessage(err, 'Print NPE Fixation failed'))
          onClose()
        }
      })()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [open, fixationId, fixationNo, triggerPrint, onClose])

  return (
    <>
      <Dialog open={open && isFetching && !previewHtml} maxWidth="xs" fullWidth>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Generating print preview…</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(previewHtml)} onClose={handleClosePreview} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title="Print Preview - NPE Fixation"
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>NPE Fixation</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>NPE Fix</Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClosePreview} size="small">Close</Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!previewHtml}
                onClick={() => {
                  const printTitle = `${sanitizePdfFileNamePart(previewKey || 'npe-fixation')}_NPE_Fixation.pdf`
                  void printAndUploadReport({
                    iframeTitle: 'NPE Fixation Preview',
                    reportName: 'rpPurchaseQryNPEFixation',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title="NPE Fixation Preview"
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(errorMessage)} onClose={() => setErrorMessage('')} maxWidth="xs" fullWidth>
        <DialogTitle>Print Error</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" size="small" onClick={() => setErrorMessage('')}>OK</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}

export interface NPEFixationListPageProps {
  embeddedContractNo?: string
}

type FilterKind = 'search'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = { key: Extract<keyof NPEFixationOut, string>; label: string; kind?: ColumnKind; align?: 'right' | 'center'; digits?: number; filter?: ColumnFilterKind; sticky?: boolean }
// Cột đơn giá không cộng (chỉ sum cột amount/quantity).
const NON_SUM_TOTAL_KEYS = new Set<string>(['price', 'price_after_interest', 'cs_fixation_id'])

// Bám đúng spec chị Đẹp gửi (ID đầu, không có Allocations badge).
// Format Access "#,##0;(#,##0);-" -> digits = 0 cho tất cả cột tiền/SL.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'cs_fixation_id', label: 'ID', kind: 'number', align: 'right', digits: 0 },
  { key: 'fixation_date', label: 'Date', kind: 'date' },
  { key: 'fixation_no', label: 'Fixation No', sticky: true },
  { key: 'contract_no', label: 'Contract No', filter: 'checkboxes' },
  { key: 'p_contract_no', label: 'P For Contract', filter: 'checkboxes' },
  { key: 'cp_code', label: 'CP Code', filter: 'checkboxes' },
  { key: 'name', label: 'CP Name', filter: 'checkboxes' },
  { key: 'quality_code', label: 'Qualty', filter: 'checkboxes' },
  { key: 'quantity_kg', label: 'Kg', kind: 'number', align: 'right', digits: 0 },
  { key: 'price', label: 'Price', kind: 'number', align: 'right', digits: 0 },
  { key: 'price_after_interest', label: 'Final Price (after Interest)', kind: 'number', align: 'right', digits: 0 },
  { key: 'to_be_paid', label: 'P - TobeRequested (vnd)', kind: 'number', align: 'right', digits: 0 },
  { key: 'total_amount', label: 'Total Amount', kind: 'number', align: 'right', digits: 0 },
  { key: 'allocated_interest', label: 'Allocated Interest', kind: 'number', align: 'right', digits: 0 },
  { key: 'allocated_amount', label: 'Allocated Advanced (incl Interest)', kind: 'number', align: 'right', digits: 0 },
  { key: 'allocated_quantity', label: 'Allocated Quantity', kind: 'number', align: 'right', digits: 0 },
  { key: 'p_paid', label: 'P - Requested (vnd)', kind: 'number', align: 'right', digits: 0 },
  { key: 'note', label: 'Fixation Note' },
  { key: 'person_update', label: 'Updated Person' },
  { key: 'date_update', label: 'Updated Date', kind: 'datetime' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessDateTime = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${formatAccessDate(v)} ${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}` }
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const cellValueText = (row: NPEFixationOut, col: ColumnDef): string => {
  const val = row[col.key as keyof NPEFixationOut]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  if (col.key === 'name') return titleCaseName(val)
  return String(val)
}

export function NPEFixationListPage({ embeddedContractNo }: NPEFixationListPageProps = {}) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const { selectedId, toggleSelect } = useRowSelection<number>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.npe-fixation')

  const [searchTerm, setSearchTerm] = useState('')
  const [contractFilter, setContractFilter] = useState(embeddedContractNo ?? '')
  const [sortState, setSortState] = useState<TableSortState<ColumnDef['key']> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: ColumnDef['key']
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters =
    !!searchTerm.trim() || (!embeddedContractNo && !!contractFilter) || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) +
    (!embeddedContractNo && contractFilter ? 1 : 0) +
    columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    if (!embeddedContractNo) setContractFilter('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<NPEFixationOut | null>(null)
  const [printTarget, setPrintTarget] = useState<NPEPrintTarget | null>(null)
  // Save & Allocate → mở NPEAllocationAddDialog với fixation + session vừa tạo
  // (Access `btnSaveAllocate_Click` → DoCmd.OpenForm frmPurchaseQryNPEAllocation_Add).
  const [pendingAlloc, setPendingAlloc] = useState<{ fixationId: number; sessionUid: string } | null>(null)

  const { data, isLoading, isFetching, error } = useListNPEFixationsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })
  const { data: editingDetail } = useGetNPEFixationQuery(editingId ?? 0, { skip: !editingId })

  const [deleteFix, { isLoading: deleting }] = useDeleteNPEFixationMutation()
  const userEmail = useAppSelector((s) => s.auth.user?.email ?? '')
  const [downloading, setDownloading] = useState(false)

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'npe-fixation',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.npe-fixation',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.length * 150),
    [visibleColumns.length],
  )

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
      if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
      if (contractFilter && row.contract_no !== contractFilter) return false
      return true
    }), [baseRows, searchTerm, columnFilters.filters, contractFilter])

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(
    () =>
      sortTableRows(
        filteredRows,
        sortState,
        visibleColumns,
        (row, key) => row[key as keyof NPEFixationOut],
      ),
    [filteredRows, sortState, visibleColumns],
  )
  const rows = useMemo(() => rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [page, rowsPerPage, sortedRows])

  const allocatedRows = useMemo(() => baseRows.filter((r) => (r.allocations_count ?? 0) > 0), [baseRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns.filter(
          (col) => col.kind === 'number' && typeof col.key === 'string' && !col.key.startsWith('_') && !NON_SUM_TOTAL_KEYS.has(String(col.key)),
        ).map((col) => [String(col.key), sumNumericColumn(filteredRows, col.key as keyof NPEFixationOut)]),
      ),
    [filteredRows, visibleColumns],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.cs_fixation_id === selectedId) ?? null, [baseRows, selectedId])
  const toggleSort = (key: ColumnDef['key']) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  const handleEdit = () => { if (selectedRow) setEditingId(selectedRow.cs_fixation_id) }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try { await deleteFix(confirmDelete.cs_fixation_id).unwrap(); snack.success(`Deleted Fixation ${confirmDelete.fixation_no}`); setConfirmDelete(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Failed to delete fixation')) }
  }
  const handlePrint = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setPrintTarget({ fixationId: selectedRow.cs_fixation_id, fixationNo: selectedRow.fixation_no ?? '' })
  }

  // Bám Access btnDownload: cần fixation có ≥1 allocation active, gọi legacy excel API,
  // lưu file vào downloads với filename từ Content-Disposition.
  const handleDownloadExcel = async () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if ((selectedRow.allocations_count ?? 0) <= 0) {
      snack.warning('This Fixation Number does not have allocated lines!')
      return
    }
    if (!userEmail) {
      snack.error('Missing user email')
      return
    }
    setDownloading(true)
    try {
      const url = `${API_BASE_URL}/erp-legacy/download/fixation/excel?token=${encodeURIComponent('JDHK-NDEB-DBCK-DGEJ')}&email=${encodeURIComponent(userEmail)}&fixation_no=${encodeURIComponent(selectedRow.fixation_no)}`
      const res = await fetch(url)
      if (!res.ok) throw new Error('Download fail!')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const disposition = res.headers.get('content-disposition') ?? ''
      const star = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
      const plain = /filename\s*=\s*([^;]+)/i.exec(disposition)
      let filename = ''
      if (star?.[1]) {
        const v = star[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
        try { filename = decodeURIComponent(v) } catch { filename = v }
      } else if (plain?.[1]) {
        filename = plain[1].trim().replace(/^"|"$/g, '')
      }
      a.href = href
      a.download = filename || `${selectedRow.fixation_no.replace(/\//g, '_')}.xlsx`
      a.click()
      URL.revokeObjectURL(href)
      snack.success('Downloaded successfully')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Download fail!'))
    } finally {
      setDownloading(false)
    }
  }

  const editDisabled = !selectedRow || (selectedRow?.allocations_count ?? 0) > 0

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết không gian flex còn lại thay vì clamp height theo viewport
        // → bỏ khoảng trống dưới bảng. Override clamp của `.app-dataframe`.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box></Button>
            <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={editDisabled || !canEdit} onClick={handleEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box></Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={editDisabled || !canEdit} onClick={() => selectedRow && setConfirmDelete(selectedRow)} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box></Button>
            <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={handlePrint} startIcon={<PrintIcon sx={{ fontSize: 16 }} />} title="Print"><Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print</Box></Button>
            <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'info.main', borderColor: 'info.main' }} disabled={downloading || !selectedRow || (selectedRow?.allocations_count ?? 0) <= 0} onClick={() => void handleDownloadExcel()} startIcon={downloading ? <CircularProgress size={11} color="inherit" /> : <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}>{downloading ? 'Exporting…' : 'Export'}</Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openMenu(e.currentTarget)} sx={filterBarBtnSx} title="Columns">
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              fontSize: '0.68rem',
              color: allocatedRows.length > 0 ? 'warning.dark' : 'transparent',
              minWidth: 0,
              flex: '0 1 auto',
            }}
          >
            {allocatedRows.length > 0
              ? `${allocatedRows.length} fixation(s) already have allocations — cannot edit/delete.`
              : ''}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={TABLE_PAGINATION_COMPACT} />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Fixation no, NPE no, supplier..."
              label="Search NPE Fixations"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(String(colPopover.key), v); setPage(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load NPE Fixations"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No NPE Fixations found"
              description="Try adjusting the table filters above."
            />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const w = getWidth(String(col.key))
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={`app-table-header-cell${col.sticky ? ' app-sticky-col' : ''}`}
                      onClick={() => toggleSort(col.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      // userSelect:'text' để bôi đen + copy tên cột (click-sort vẫn chạy).
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', position: 'relative' }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {visibleColumns.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatNumberFixed(total, col.digits ?? 0)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={col.sticky ? 'app-sticky-col' : undefined}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: NPEFixationOut) =>
              visibleColumns.map((col) => {
                const val = cellValueText(row, col)
                const w = getWidth(String(col.key))
                const wrap = getWidth(String(col.key)) != null
                if (wrap) {
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className={col.sticky ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                    </TableCell>
                  )
                }
                return (
                  <TableCell
                    key={String(col.key)}
                    align={col.align}
                    className={col.sticky ? 'app-sticky-col' : undefined}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                  >
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.cs_fixation_id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) toggleSelect(id) }} />
              },
            }}
          />
          )}
        </Box>
      </Box>

      <NPEFixationFormDialog
        open={createOpen}
        mode="create"
        defaultNpeContractNo={embeddedContractNo}
        onClose={() => setCreateOpen(false)}
        onSaveAllocate={(out) => setPendingAlloc({ fixationId: out.cs_fixation_id, sessionUid: out.session_uid })}
      />
      <NPEFixationFormDialog open={!!editingId && !!editingDetail?.data} mode="edit" initialValue={editingDetail?.data ?? null} onClose={() => setEditingId(null)} />
      <NPEAllocationAddDialog
        open={!!pendingAlloc}
        initialFixationId={pendingAlloc?.fixationId}
        initialSessionUid={pendingAlloc?.sessionUid}
        onClose={() => setPendingAlloc(null)}
        onCommitted={() => setPendingAlloc(null)}
      />
      <ConfirmDialog open={!!confirmDelete} title="Delete Fixation" message={confirmDelete ? `Are you sure you want to delete Fixation ${confirmDelete.fixation_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDelete(null)} />
      <NPEFixationPrintDialog
        open={Boolean(printTarget)}
        printTarget={printTarget}
        canEdit={canEdit}
        onClose={() => setPrintTarget(null)}
      />
    </Box>
  )
}

export default NPEFixationListPage
